import { deriveDisplaySeverity } from '../../../../../../governance';
import type { UnifiedQuestionRow } from './buildUnifiedRows';

/**
 * FamilyTriageItem — per-familyId rollup powering the Seed Prioritizer.
 *
 * Used in the Overzicht tab to answer: "Which seed file should I open first?"
 * Sorted by urgencyScore (desc). urgencyScore mirrors the weights used in
 * computePriority() in shared.tsx so that the dashboard and the per-question
 * priority indicator agree.
 */
export interface FamilyTriageItem {
  familyId: string;
  moduleCode: string;
  total: number;
  pass: number;
  fail: number;
  review: number;
  criticalCount: number;
  blockerCount: number;
  reviewCount: number;
  warningCount: number;
  avgScore: number;
  worstSeverity: number;
  urgencyScore: number;
  /** Top 3 most-frequent issue codes within this family. */
  topIssueCodes: string[];
}
interface Acc {
  familyId: string;
  moduleCode: string;
  total: number;
  pass: number;
  fail: number;
  review: number;
  criticalCount: number;
  blockerCount: number;
  reviewCount: number;
  warningCount: number;
  scoreSum: number;
  worstSeverity: number;
  codeCounts: Map<string, number>;
}

/**
 * Compute the family triage list from unified rows.
 *
 * Urgency-score weights (mirror computePriority in shared.tsx):
 *   critical × 10000 + blocker × 1000 + review × 200 + warning × 50
 *   + worstSeverity + (100 - avgScore)
 */
export function computeFamilyTriage(rows: UnifiedQuestionRow[]): FamilyTriageItem[] {
  if (rows.length === 0) return [];
  const byFamily = new Map<string, Acc>();
  for (const row of rows) {
    const fid = row.familyId || 'UNKNOWN';
    let acc = byFamily.get(fid);
    if (!acc) {
      acc = {
        familyId: fid,
        moduleCode: row.moduleCode,
        total: 0,
        pass: 0,
        fail: 0,
        review: 0,
        criticalCount: 0,
        blockerCount: 0,
        reviewCount: 0,
        warningCount: 0,
        scoreSum: 0,
        worstSeverity: 0,
        codeCounts: new Map()
      };
      byFamily.set(fid, acc);
    }
    acc.total += 1;
    acc.scoreSum += row.pipelineScore;
    if (row.verdict === 'PASS') acc.pass += 1;else if (row.verdict === 'FAIL') acc.fail += 1;else acc.review += 1;
    for (const issue of row.pipelineIssues) {
      const ds = deriveDisplaySeverity(issue.kind, issue.severity);
      if (ds === 'critical') acc.criticalCount += 1;else if (ds === 'blocker') acc.blockerCount += 1;else if (ds === 'review') acc.reviewCount += 1;else if (ds === 'coach') acc.warningCount += 1;
      if (issue.severity > acc.worstSeverity) acc.worstSeverity = issue.severity;
      if (issue.kind !== 'info') {
        acc.codeCounts.set(issue.code, (acc.codeCounts.get(issue.code) ?? 0) + 1);
      }
    }
  }
  return Array.from(byFamily.values()).map(acc => {
    const topIssueCodes = Array.from(acc.codeCounts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([code]) => code);
    const avgScore = acc.total > 0 ? Math.round(acc.scoreSum / acc.total) : 0;
    const urgencyScore = acc.criticalCount * 10000 + acc.blockerCount * 1000 + acc.reviewCount * 200 + acc.warningCount * 50 + acc.worstSeverity + (100 - avgScore);
    return {
      familyId: acc.familyId,
      moduleCode: acc.moduleCode,
      total: acc.total,
      pass: acc.pass,
      fail: acc.fail,
      review: acc.review,
      criticalCount: acc.criticalCount,
      blockerCount: acc.blockerCount,
      reviewCount: acc.reviewCount,
      warningCount: acc.warningCount,
      avgScore,
      worstSeverity: acc.worstSeverity,
      urgencyScore,
      topIssueCodes
    };
  }).sort((a, b) => b.urgencyScore - a.urgencyScore);
}